/**
 * src/components/mobile/MobileProfile.jsx
 * Route: /mobile/profile
 * Handles: My Profile · Edit Profile · Change Password
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import toast from 'react-hot-toast';

const API      = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const IMG_BASE = API.replace('/api', '');
const auth     = () => ({
  Authorization: `Bearer ${localStorage.getItem('iassets_token')}`,
  'Content-Type': 'application/json',
});

const IS = {
  width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10,
  padding: '11px 14px', fontSize: 14, outline: 'none',
  boxSizing: 'border-box', background: '#fff', color: '#1a1f2e',
  WebkitAppearance: 'none',
};
const LS = { fontSize: 12, fontWeight: 700, color: '#475569',
  display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' };

export default function MobileProfile() {
  const navigate = useNavigate();
  const [tab, setTab]       = useState('view');   // view | edit | password

  /* Listen for tab switch events from MobileLayout profile menu */
  useEffect(() => {
    const fn = e => setTab(e.detail);
    window.addEventListener('mobile-profile-tab', fn);
    return () => window.removeEventListener('mobile-profile-tab', fn);
  }, []);
  const [user, setUser]     = useState(() => {
    try { return JSON.parse(localStorage.getItem('iassets_user') || '{}'); }
    catch { return {}; }
  });
  const [form, setForm]     = useState({
    name: '', phone: '', department: '', designation: '', functional_area: ''
  });
  const [pwForm, setPwForm] = useState({ current: '', newpw: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  /* ── Resolve avatar URL ── */
  const avatarSrc = (() => {
    const a = user.avatar || user.profile_picture || user.photo || '';
    if (!a) return null;
    if (a.startsWith('http')) return a;                        // full URL
    if (a.startsWith('/'))    return `${IMG_BASE}${a}`;        // has path
    return `${IMG_BASE}/uploads/avatars/${a}`;                 // bare filename
  })();

  /* ── Load user from API ── */
  useEffect(() => {
    /* Try /auth/me first (standard endpoint), fallback to /users/me */
    axios.get(`${API}/auth/me`, { headers: auth() })
      .catch(() => axios.get(`${API}/users/me`, { headers: auth() }))
      .then(r => {
        const u = r.data || {};
        setUser(u);
        setForm({
          name:            u.name            || '',
          phone:           u.phone           || '',
          department:      u.department      || '',
          designation:     u.designation     || '',
          functional_area: u.functional_area || '',
        });
        localStorage.setItem('iassets_user', JSON.stringify(u));
      })
      .catch(() => {
        setForm({
          name:            user.name            || '',
          phone:           user.phone           || '',
          department:      user.department      || '',
          designation:     user.designation     || '',
          functional_area: user.functional_area || '',
        });
      });
  }, []);

  /* ── Save profile ── */
  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      /* Try PATCH /auth/me first, then PUT /users/me as fallback */
      let r;
      try {
        r = await axios.patch(`${API}/profile`, form, { headers: auth() });
      } catch {
        r = await axios.put(`${API}/profile`, form, { headers: auth() });
      }
      const updated = r?.data || { ...user, ...form };
      localStorage.setItem('iassets_user', JSON.stringify(updated));
      setUser(updated);
      toast.success('Profile updated');
      setTab('view');
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Update failed — check with admin');
    } finally { setSaving(false); }
  };

  /* ── Change password ── */
  const changePassword = async () => {
    if (!pwForm.current)  { toast.error('Enter current password'); return; }
    if (!pwForm.newpw)    { toast.error('Enter new password'); return; }
    if (pwForm.newpw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pwForm.newpw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.newpw },
        { headers: auth() }
      );
      toast.success('Password changed successfully');
      setPwForm({ current:'', newpw:'', confirm:'' });
      setTab('view');
    } catch(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Failed to change password';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const TABS = [
    { id:'view',     label:'My Profile'   },
    { id:'edit',     label:'Edit Profile' },
    { id:'password', label:'Change Password' },
  ];

  return (
    <MobileLayout active="home" showBack title="Profile">
      <div style={{ padding:'12px 14px' }}>

        {/* Avatar + name */}
        <div style={{ background:'#fff', borderRadius:14, padding:'20px 16px',
          marginBottom:12, display:'flex', alignItems:'center', gap:14,
          boxShadow:'0 1px 6px rgba(0,0,0,.06)' }}>
          <div style={{ width:60, height:60, borderRadius:'50%',
            background:'#3b82f6', display:'flex', alignItems:'center',
            justifyContent:'center', color:'#fff', fontSize:22, fontWeight:800,
            flexShrink:0, overflow:'hidden',
            border:'3px solid #eff6ff' }}>
            {avatarSrc
              ? <img src={avatarSrc} alt={user.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover' }}
                  onError={e => { e.target.style.display='none'; }}/>
              : (user.name?.[0]?.toUpperCase() || 'U')}
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1a1f2e' }}>{user.name || '—'}</div>
            <div style={{ fontSize:12, color:'#94a3b8', marginTop:3 }}>{user.email || '—'}</div>
            {user.designation && (
              <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{user.designation}</div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', background:'#fff', borderRadius:12,
          padding:4, marginBottom:16, gap:2,
          boxShadow:'0 1px 6px rgba(0,0,0,.06)' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex:1, padding:'9px 4px', border:'none', borderRadius:9,
                fontSize:11, fontWeight:tab===t.id?700:500, cursor:'pointer',
                background: tab===t.id ? '#1a56db' : 'transparent',
                color: tab===t.id ? '#fff' : '#64748b',
                transition:'all .15s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── MY PROFILE tab ── */}
        {tab === 'view' && (
          <div style={{ background:'#fff', borderRadius:14,
            boxShadow:'0 1px 6px rgba(0,0,0,.06)', overflow:'hidden' }}>
            {[
              ['Full Name',      user.name            || '—'],
              ['Email Address',  user.email           || '—'],
              ['Phone',          user.phone           || '—'],
              ['Department',     user.department      || '—'],
              ['Designation',    user.designation     || '—'],
              ['Functional Area',user.functional_area || '—'],
              ['Role',           user.role_name || user.role || '—'],
              ['Organisation',   user.org_name || user.organisation || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                borderBottom:'1px solid #f8fafc' }}>
                <span style={{ fontSize:13, color:'#94a3b8', width:120, flexShrink:0 }}>{label}</span>
                <span style={{ fontSize:13, fontWeight:600, color:'#1a1f2e',
                  flex:1, textAlign:'right' }}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── EDIT PROFILE tab ── */}
        {tab === 'edit' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:'#fff', borderRadius:14, padding:16,
              boxShadow:'0 1px 6px rgba(0,0,0,.06)',
              display:'flex', flexDirection:'column', gap:14 }}>

              {/* Editable fields */}
              {[
                { label:'Full Name *',      key:'name',            type:'text',  placeholder:'Your full name'   },
                { label:'Phone',            key:'phone',           type:'tel',   placeholder:'9860340338'       },
                { label:'Department',       key:'department',      type:'text',  placeholder:'e.g. IT'          },
                { label:'Designation',      key:'designation',     type:'text',  placeholder:'e.g. CEO'         },
                { label:'Functional Area',  key:'functional_area', type:'text',  placeholder:'e.g. Operations'  },
              ].map(f => (
                <div key={f.key}>
                  <label style={LS}>{f.label}</label>
                  <input type={f.type} value={form[f.key]} placeholder={f.placeholder}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={IS}/>
                </div>
              ))}

              {/* Read-only fields */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div>
                  <label style={LS}>Email Address</label>
                  <div style={{ fontSize:14, color:'#64748b', padding:'11px 0' }}>{user.email || '—'}</div>
                </div>
                <div>
                  <label style={LS}>Role</label>
                  <div style={{ fontSize:14, color:'#64748b', padding:'11px 0' }}>{user.role_name || user.role || '—'}</div>
                </div>
                <div>
                  <label style={LS}>Organisation</label>
                  <div style={{ fontSize:14, color:'#64748b', padding:'11px 0' }}>{user.org_name || user.organisation || '—'}</div>
                </div>
              </div>
            </div>

            <button onClick={saveProfile} disabled={saving}
              style={{ padding:'14px', background:saving?'#93c5fd':'#1a56db',
                color:'#fff', border:'none', borderRadius:12,
                fontSize:15, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
            <button onClick={() => setTab('view')}
              style={{ padding:'12px', background:'#f8fafc', color:'#64748b',
                border:'1px solid #e2e8f0', borderRadius:12,
                fontSize:14, fontWeight:600, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        )}

        {/* ── CHANGE PASSWORD tab ── */}
        {tab === 'password' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* Info banner — matches desktop */}
            <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe',
              borderRadius:10, padding:'12px 14px',
              display:'flex', alignItems:'flex-start', gap:10 }}>
              <span style={{ fontSize:18, flexShrink:0 }}>🔒</span>
              <span style={{ fontSize:13, color:'#1a56db', lineHeight:1.5 }}>
                Password must be at least 6 characters long.
              </span>
            </div>

            <div style={{ background:'#fff', borderRadius:14, padding:16,
              boxShadow:'0 1px 6px rgba(0,0,0,.06)',
              display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'Current Password *',  key:'current', placeholder:'Enter current password'  },
                { label:'New Password *',       key:'newpw',   placeholder:'Enter new password'      },
                { label:'Confirm New Password *',key:'confirm',placeholder:'Re-enter new password'   },
              ].map(f => (
                <div key={f.key}>
                  <label style={LS}>{f.label}</label>
                  <input type="password" value={pwForm[f.key]} placeholder={f.placeholder}
                    onChange={e => setPwForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={IS}/>
                </div>
              ))}
            </div>

            <button onClick={changePassword} disabled={saving}
              style={{ padding:'14px', background:saving?'#93c5fd':'#1a56db',
                color:'#fff', border:'none', borderRadius:12,
                fontSize:15, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
              {saving ? 'Changing…' : '🔑 Change Password'}
            </button>
            <button onClick={() => setTab('view')}
              style={{ padding:'12px', background:'#f8fafc', color:'#64748b',
                border:'1px solid #e2e8f0', borderRadius:12,
                fontSize:14, fontWeight:600, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        )}

      </div>
    </MobileLayout>
  );
}
